import { createSlice } from '@reduxjs/toolkit';

// ============================================================================
// CONSTANTS
// ============================================================================

export const FILE_STATUS = {
  READY: 'ready',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const UPLOAD_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  UPLOADING: 'uploading',
  COMPLETED: 'completed',
  ERROR: 'error',
};

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  status: UPLOAD_STATUS.IDLE,

  // Files list
  files: [],

  // Processing progress
  processing: { current: 0, total: 0, percent: 0 },

  // Upload progress
  upload: { completed: 0, failed: 0, total: 0, percent: 0 },

  // Duplicates (by filename)
  duplicates: [],

  // Error message
  error: null,

  // UI state
  ui: { isMinimized: false },
};

// ============================================================================
// SLICE
// ============================================================================

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    // Processing
    startProcessing(state, action) {
      state.status = UPLOAD_STATUS.PROCESSING;
      state.processing = { current: 0, total: action.payload.total, percent: 0 };
      state.error = null;
    },

    updateProcessingProgress(state, action) {
      const { current, total } = action.payload;
      state.processing = {
        current,
        total,
        percent: total > 0 ? Math.round((current / total) * 100) : 0,
      };
    },

    processingComplete(state, action) {
      const { files, duplicates = [] } = action.payload;
      // Append new files to existing files (don't replace)
      state.files = [...state.files, ...files];
      // Append new duplicates to existing duplicates
      state.duplicates = [...state.duplicates, ...duplicates];
      state.processing = { current: 0, total: 0, percent: 0 };
      state.status = UPLOAD_STATUS.IDLE;
    },

    // Upload
    startUpload(state) {
      // Only upload files that are valid AND not already uploaded (READY status)
      const pendingFiles = state.files.filter((f) => f.isValid && f.status === FILE_STATUS.READY);
      pendingFiles.forEach((f) => {
        f.status = FILE_STATUS.UPLOADING;
      });
      state.status = UPLOAD_STATUS.UPLOADING;
      state.upload = { completed: 0, failed: 0, total: pendingFiles.length, percent: 0 };
      state.error = null;
    },

    updateFileStatus(state, action) {
      const { fileId, status, error } = action.payload;
      const file = state.files.find((f) => f.id === fileId);
      if (file) {
        file.status = status;
        if (error) file.error = error;
      }
    },

    updateUploadProgress(state, action) {
      const { completed, failed, total } = action.payload;
      state.upload = {
        completed,
        failed,
        total,
        percent: total > 0 ? Math.round(((completed + failed) / total) * 100) : 0,
      };
    },

    uploadComplete(state, action) {
      state.status = state.upload.failed > 0 ? UPLOAD_STATUS.ERROR : UPLOAD_STATUS.COMPLETED;
      state.upload.percent = 100;
    },

    // File Management
    removeFile(state, action) {
      state.files = state.files.filter((f) => f.id !== action.payload);
    },

    removeAllFiles(state) {
      state.files = [];
      state.duplicates = [];
      state.status = UPLOAD_STATUS.IDLE;
      state.upload = initialState.upload;
    },

    removeDuplicates(state) {
      // Just clear the duplicates warning - duplicates were already skipped during processing
      state.duplicates = [];
    },

    // Error
    setError(state, action) {
      state.error = action.payload;
      state.status = UPLOAD_STATUS.ERROR;
    },

    clearError(state) {
      state.error = null;
    },

    // Reset
    resetUpload(state) {
      state.files.forEach((f) => {
        if (f.isValid) f.status = FILE_STATUS.READY;
      });
      state.status = UPLOAD_STATUS.IDLE;
      state.upload = initialState.upload;
      state.error = null;
    },

    resetAll() {
      return initialState;
    },

    // UI
    setMinimized(state, action) {
      state.ui.isMinimized = action.payload;
    },
  },
});

// ============================================================================
// EXPORTS
// ============================================================================

export const {
  startProcessing,
  updateProcessingProgress,
  processingComplete,
  startUpload,
  updateFileStatus,
  updateUploadProgress,
  uploadComplete,
  removeFile,
  removeAllFiles,
  removeDuplicates,
  setError,
  clearError,
  resetUpload,
  resetAll,
  setMinimized,
} = uploadSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

export const selectStatus = (state) => state.upload.status;
export const selectFiles = (state) => state.upload.files;
export const selectProcessing = (state) => state.upload.processing;
export const selectUploadProgress = (state) => state.upload.upload;
export const selectDuplicates = (state) => state.upload.duplicates;
export const selectError = (state) => state.upload.error;
export const selectUI = (state) => state.upload.ui;
export const selectStats = (state) => ({
  valid: state.upload.files.filter((f) => f.isValid).length,
  invalid: state.upload.files.filter((f) => !f.isValid).length,
  total: state.upload.files.length,
});

export const selectValidFiles = (state) => state.upload.files.filter((f) => f.isValid);
export const selectValidCount = (state) => state.upload.files.filter((f) => f.isValid).length;
// Pending files: valid files that are READY to upload (not already uploaded)
export const selectPendingFiles = (state) =>
  state.upload.files.filter((f) => f.isValid && f.status === FILE_STATUS.READY);
export const selectPendingCount = (state) =>
  state.upload.files.filter((f) => f.isValid && f.status === FILE_STATUS.READY).length;
export const selectIsProcessing = (state) => state.upload.status === UPLOAD_STATUS.PROCESSING;
export const selectIsUploading = (state) => state.upload.status === UPLOAD_STATUS.UPLOADING;

export default uploadSlice.reducer;
